import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function LoginPage() {
  const { login, resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const { register, handleSubmit, formState: { errors }, getValues } = useForm();

  const onLogin = async (data) => {
    setLoading(true);
    try {
      await login(data.email, data.password);
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (err) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        toast.error('Invalid email or password.');
      } else {
        toast.error(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const onReset = async () => {
    const email = getValues('email');
    if (!email) { toast.error('Enter your email first.'); return; }
    setLoading(true);
    try {
      await resetPassword(email);
      toast.success('Password reset email sent — check your inbox.');
      setResetMode(false);
    } catch {
      toast.error('Could not send reset email. Check the address and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        {/* Logo / brand */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-4">
            <img src="/images/mainlogo.jpg" alt="The Hub Crookwell" className="h-48 mx-auto" />
          </Link>
          <h1 className="text-2xl font-extrabold text-slate-800">
            {resetMode ? 'Reset Password' : 'Welcome back'}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {resetMode ? "We'll email you a reset link." : "Sign in to manage your bookings."}
          </p>
        </div>

        <div className="card p-8">
          <form onSubmit={handleSubmit(resetMode ? onReset : onLogin)} className="space-y-4">
            <div>
              <label className="label">Email Address</label>
              <input
                type="email"
                className="input-field"
                placeholder="you@example.com"
                {...register('email', { required: 'Required', pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' } })}
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
            </div>

            {!resetMode && (
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="label mb-0">Password</label>
                  <button
                    type="button"
                    onClick={() => setResetMode(true)}
                    className="text-xs text-brand-500 hover:text-brand-700 font-medium"
                  >
                    Forgot password?
                  </button>
                </div>
                <input
                  type="password"
                  className="input-field"
                  placeholder="••••••••"
                  {...register('password', { required: 'Required' })}
                />
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
              </div>
            )}

            <button
              type={resetMode ? 'button' : 'submit'}
              onClick={resetMode ? onReset : undefined}
              disabled={loading}
              className="btn-primary w-full mt-2"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {resetMode ? 'Sending…' : 'Signing in…'}
                </span>
              ) : resetMode ? 'Send Reset Link' : 'Sign In'}
            </button>

            {resetMode && (
              <button type="button" onClick={() => setResetMode(false)} className="btn-secondary w-full">
                Back to Sign In
              </button>
            )}
          </form>

          {!resetMode && (
            <p className="text-center text-sm text-slate-500 mt-6">
              Don't have an account?{' '}
              <Link to="/book" className="text-brand-500 font-medium hover:text-brand-700">
                Book your first space
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
